import { isAuditFieldNotApplicable } from './auditFieldApplicability.js';
import { getScoreStatus } from './scoreUtils.js';

/**
 * @param {unknown} value
 * @param {number} scaleMax
 * @returns {number}
 */
function clampRangeValue(value, scaleMax = 100) {
    const num = Number(value);
    if (!Number.isFinite(num)) {
        return NaN;
    }
    const max = Number.isFinite(scaleMax) && scaleMax > 0 ? scaleMax : 100;
    return Math.min(Math.max(num, 0), max);
}

/**
 * @param {Record<string, unknown>} field
 * @returns {{ earned: number, max: number }}
 */
function scoreRangeField(field) {
    const maxScore = Number(field?.max_score ?? 0);
    if (!Number.isFinite(maxScore) || maxScore <= 0) {
        return { earned: 0, max: 0 };
    }

    const scaleMax = Number(field?.params?.max ?? 100);
    const clamped = clampRangeValue(field?.value, scaleMax);

    if (!Number.isFinite(clamped)) {
        return { earned: 0, max: maxScore };
    }

    const earned = (clamped / 100) * maxScore;
    return {
        earned: Math.round(earned * 100) / 100,
        max: maxScore,
    };
}

/**
 * @param {Record<string, unknown>} field
 * @returns {{ earned: number, max: number }}
 */
function scoreChoiceField(field) {
    const { value, scoring_rules, max_score, timer, threshold } = field;

    const maxScore = Number(max_score ?? 0);
    if (!Number.isFinite(maxScore) || maxScore <= 0) {
        return { earned: 0, max: 0 };
    }

    const values = Array.isArray(value) ? value : [value];

    if (isAuditFieldNotApplicable(field)) {
        return { earned: 0, max: 0 };
    }

    if (!Array.isArray(scoring_rules) || scoring_rules.length === 0) {
        return { earned: 0, max: maxScore };
    }

    const rules = [...scoring_rules];
    const isCorrect = values.some((v) => rules.includes(v));

    if (!isCorrect) {
        return { earned: 0, max: maxScore };
    }

    if (threshold) {
        const thresholdNum = Number(threshold);
        if (thresholdNum > 0) {
            const halfScore = maxScore / 2;
            const timerValue = Number(timer ?? 0);
            const timePercentage = Math.min(timerValue / thresholdNum, 1);
            const timerScore = timePercentage * halfScore;

            return {
                earned: Math.round((halfScore + timerScore) * 100) / 100,
                max: maxScore,
            };
        }
    }

    return { earned: maxScore, max: maxScore };
}

/**
 * Client-side audit field scoring (same rules as saving an audit in AuditTemplateContent).
 * Used when persisting form_score / form_max_score and when building section score cards.
 *
 * @param {Array<Record<string, unknown>>} fields
 * @returns {{ totalScore: number, totalMaxPossible: number }}
 */
export function calculateScoreForFields(fields) {
    if (!Array.isArray(fields)) {
        return { totalScore: 0, totalMaxPossible: 0 };
    }

    let totalScore = 0;
    let totalMaxPossible = 0;

    fields.forEach((field) => {
        if (!field?.max_score) return;

        const result =
            field?.type === 'range'
                ? scoreRangeField(field)
                : scoreChoiceField(field);

        totalScore += result.earned;
        totalMaxPossible += result.max;
    });

    return {
        totalScore: Math.round(totalScore * 100) / 100,
        totalMaxPossible,
    };
}

/**
 * WidgetCard preset from section percentage (0–100).
 * @param {number} percent
 * @returns {'success' | 'warning' | 'danger'}
 */
export function presetForSectionPercent(percent) {
    const p = Number(percent);
    if (!Number.isFinite(p)) return 'danger';
    const status = getScoreStatus(p);
    return status === 'neutral' ? 'info' : status;
}
